import { useEffect, useRef, useState } from "react";
import type { NormalizedRecord } from "./types";

interface FormState {
  content: string;
  theme: string;
  category: string;
  priority: string;
  status: string;
}

interface Props {
  record: NormalizedRecord | null;
  statusOptions: string[];
  knownThemes?: string[];
  knownCategories?: string[];
  categoryByTheme?: Record<string, string>;
  mappingHasErrors?: boolean;
  onClose: () => void;
  onDirtyChange?: (dirty: boolean) => void;
  onSubmit: (input: {
    content?: string;
    theme?: string;
    category?: string;
    priority?: string;
    status?: string;
  }) => Promise<void>;
}

function recordToForm(record: NormalizedRecord): FormState {
  return {
    content: record.content,
    theme: record.theme,
    category: record.category,
    priority: record.priority === null ? "" : String(record.priority),
    status: record.status,
  };
}

const EMPTY_FORM: FormState = {
  content: "",
  theme: "",
  category: "",
  priority: "",
  status: "收集",
};

/**
 * Spec §5.5 / §5.6: edit dialog that uses the browser-native <dialog> for
 * focus management and Escape-to-close. On >=768px the dialog renders inline;
 * below 768px it stretches full-width, matching the spec breakpoint.
 */
export function RecordDetailDialog({
  record,
  statusOptions,
  knownThemes = [],
  knownCategories = [],
  categoryByTheme = {},
  mappingHasErrors = false,
  onClose,
  onDirtyChange,
  onSubmit,
}: Props) {
  const dialogRef = useRef<HTMLDialogElement | null>(null);
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [initialForm, setInitialForm] = useState<FormState>(EMPTY_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [categoryTouched, setCategoryTouched] = useState(false);
  const dirty = JSON.stringify(form) !== JSON.stringify(initialForm);

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;
    if (record && !dialog.open) {
      const nextForm = recordToForm(record);
      setForm(nextForm);
      setInitialForm(nextForm);
      setCategoryTouched(false);
      setError(null);
      setSuccess(null);
      dialog.showModal();
    } else if (!record && dialog.open) {
      dialog.close();
    }
  }, [record]);

  useEffect(() => {
    onDirtyChange?.(Boolean(record) && dirty);
  }, [dirty, onDirtyChange, record]);

  function handleClose() {
    if (submitting) return;
    if (dirty && !window.confirm("有未保存的更改，确定要放弃吗？")) return;
    onDirtyChange?.(false);
    dialogRef.current?.close();
    onClose();
  }

  async function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!record) return;
    if (!form.content.trim()) {
      setError("内容不能为空。");
      return;
    }
    setSubmitting(true);
    setError(null);
    setSuccess(null);
    try {
      await onSubmit({
        content: form.content.trim(),
        theme: form.theme.trim(),
        category: form.category.trim(),
        priority: form.priority.trim(),
        status: form.status,
      });
      setInitialForm(form);
      setSuccess("保存成功");
    } catch (err) {
      setError(err instanceof Error ? err.message : "保存失败");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <dialog
      ref={dialogRef}
      aria-labelledby="record-detail-title"
      onCancel={(event) => {
        event.preventDefault();
        handleClose();
      }}
      className="record-dialog"
    >
      {record ? (
        <form onSubmit={handleSubmit} noValidate className="space-y-3 p-4">
          <header className="flex items-center justify-between">
            <h2 id="record-detail-title" className="text-base font-semibold">编辑记录</h2>
            {record.isConfigRecord ? (
              <span className="rounded border border-amber-400 px-1 text-xs text-amber-900">
                系统配置
              </span>
            ) : null}
          </header>

          {record.isConfigRecord ? (
            <p className="rounded bg-amber-50 p-2 text-xs text-amber-900">
              修改正文会影响后续主题类别自动回填。
            </p>
          ) : null}

          <label className="block text-sm">
            <span className="mb-1 block text-neutral-700">正文</span>
            <textarea
              className="w-full rounded border border-neutral-300 p-2 text-sm"
              rows={5}
              value={form.content}
              onChange={(e) => {
                setSuccess(null);
                setForm({ ...form, content: e.target.value });
              }}
              required
            />
          </label>

          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            <label className="block text-sm">
              <span className="mb-1 block text-neutral-700">主题</span>
              <input
                type="text"
                list="record-detail-themes"
                className="w-full rounded border border-neutral-300 p-2 text-sm"
                value={form.theme}
                onChange={(e) => {
                  const theme = e.target.value;
                  const suggested = mappingHasErrors ? undefined : categoryByTheme[theme];
                  setSuccess(null);
                  setForm({
                    ...form,
                    theme,
                    category: !categoryTouched && suggested ? suggested : form.category,
                  });
                }}
              />
              <datalist id="record-detail-themes">
                {knownThemes.map((theme) => <option key={theme} value={theme} />)}
              </datalist>
            </label>
            <label className="block text-sm">
              <span className="mb-1 block text-neutral-700">类别</span>
              <input
                type="text"
                list="record-detail-categories"
                className="w-full rounded border border-neutral-300 p-2 text-sm"
                value={form.category}
                onChange={(e) => {
                  setCategoryTouched(true);
                  setSuccess(null);
                  setForm({ ...form, category: e.target.value });
                }}
              />
              <datalist id="record-detail-categories">
                {knownCategories.map((category) => <option key={category} value={category} />)}
              </datalist>
            </label>
            <label className="block text-sm">
              <span className="mb-1 block text-neutral-700">优先级</span>
              <input
                type="number"
                className="w-full rounded border border-neutral-300 p-2 text-sm"
                value={form.priority}
                onChange={(e) => {
                  setSuccess(null);
                  setForm({ ...form, priority: e.target.value });
                }}
              />
            </label>
            <label className="block text-sm">
              <span className="mb-1 block text-neutral-700">状态</span>
              <select
                className="w-full rounded border border-neutral-300 p-2 text-sm"
                value={form.status}
                onChange={(e) => {
                  setSuccess(null);
                  setForm({ ...form, status: e.target.value });
                }}
              >
                {statusOptions.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
            </label>
          </div>

          {mappingHasErrors ? (
            <p className="form-warning">主题类别映射存在冲突，类别不会自动建议。</p>
          ) : null}

          {record.detailUrl ? (
            <a
              href={record.detailUrl}
              rel="noreferrer"
              target="_blank"
              className="inline-block text-xs text-blue-600 underline"
            >
              详情链接
            </a>
          ) : null}

          {error ? (
            <p role="alert" className="text-sm text-red-600">
              {error}
            </p>
          ) : null}
          {success ? <p role="status" className="form-success">{success}</p> : null}

          <footer className="flex justify-end gap-2">
            <button
              type="button"
              onClick={handleClose}
              disabled={submitting}
              className="rounded border border-neutral-300 px-3 py-1 text-sm"
            >
              取消
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="rounded bg-neutral-900 px-3 py-1 text-sm text-white disabled:opacity-50"
            >
              {submitting ? "保存中…" : "保存"}
            </button>
          </footer>
        </form>
      ) : null}
    </dialog>
  );
}
